import * as React from 'react';
// @ts-expect-error
import {components, mergeStyles} from 'react-select';
// @ts-expect-error
import type {OptionProps, SelectBaseProps} from 'react-select';

import {LeadStatusIcon} from '../icon/lead-status-icon';
import type {Status} from '../icon/lead-status-icon';
import {SelectPickerGeneric} from '../select-picker-generic/select-picker-generic';

import styles from './select-lead-status.module.css';

function makeOptions(leadId: string) {
    return [
        {
            label: 'Open',
            value: 'OPEN',
            actionType: 'open',
            url: `/lead/reopen-lead/${leadId}.json`,
        },
        {
            label: 'Won',
            value: 'WON',
            actionType: 'close',
            url: `/lead/close-lead/${leadId}.json?closeStatus=win`,
        },
        {
            label: 'Lost',
            value: 'LOST',
            actionType: 'close',
            url: `/lead/close-lead/${leadId}.json?closeStatus=lose`,
        },
        {
            label: 'Cancelled',
            value: 'CANCELLED',
            actionType: 'close',
            url: `/lead/close-lead/${leadId}.json?closeStatus=cancel`,
        },
        {
            label: 'Pending',
            value: 'PENDING',
            actionType: 'open',
            url: `/lead/pend-lead/${leadId}.json`,
        },
    ];
}

type Props = SelectBaseProps & {
    leadId: string;
    value: string; // LeadStatus,
    placeholder?: string;
    includeControlIcon?: boolean;
};

type Option = {
    value: Status;
    label: string;
    actionType: 'open' | 'close';
    url: string;
};

/**
 * Use this to render a dropdown to choose a single assignee.
 */
export function SelectLeadStatus(props: Props) {
    const {
        leadId,
        value,
        styles: localStyles,
        placeholder,
        classNamePrefix,
        includeControlIcon,
    } = props;

    // We are adding a number of hidden divs to "click" on in our change handler so that we can use old-style
    // ajax popovers
    const clickableDivRefs = React.useRef<{
        [status: string]: HTMLElement | null | undefined;
    }>({});

    const options = React.useMemo(() => makeOptions(leadId), [leadId]);
    const currentStatus = React.useMemo(
        () => options.find((opt) => opt.value === value),
        [options, value]
    );

    const baseStyles = React.useMemo(
        () => ({
            // @ts-expect-error
            control: (base) => ({...base, minWidth: 140}),
            // @ts-expect-error
            menu: (base) => ({...base, minWidth: 140, zIndex: 5}),
        }),
        []
    );

    let mergedStyles = baseStyles;
    if (styles) {
        mergedStyles = mergeStyles(mergedStyles, localStyles);
    }

    const ControlIconComponent = React.useCallback(() => {
        switch (value) {
            case 'WON':
                return <img src='/include/images/lead-icons/won.svg' width={16} />;
            case 'LOST':
                return <img src='/include/images/lead-icons/lost.svg' width={16} />;
            case 'CANCELLED':
                return <img src='/include/images/lead-icons/cancelled.svg' width={16} />;
            case 'PENDING':
                return <img src='/include/images/lead-icons/pending.svg' width={16} />;
            case 'OPEN':
            default: {
                return <img src='/avatar/leads/v1/0/1/1/0' width={16} />;
            }
        }
    }, [value]);

    const handleChange = React.useCallback((option: Option) => {
        if (option.actionType === 'open') {
            Nut.ajax.post({url: option.url});
        } else {
            const clickTarget = clickableDivRefs.current[option.value];

            if (clickTarget) {
                clickTarget.click();
            }
        }
    }, []);

    const isOptionSelected = React.useCallback((option: Option, selectedValues: Option[]) => {
        // @ts-expect-error
        return option.value === selectedValues[0];
    }, []);

    return (
        <>
            <SelectPickerGeneric
                className='control-segment popover-opener-south'
                classNamePrefix={classNamePrefix}
                options={options}
                isOptionSelected={isOptionSelected}
                openMenuOnFocus={true}
                placeholder={placeholder || 'Status…'}
                value={currentStatus}
                backspaceRemovesValue={false}
                tabSelectsValue={false}
                onChange={handleChange}
                isSearchable={false}
                hideSelectedOptions={shouldHideSelectedOptions(value)}
                styles={mergedStyles}
                controlIcon={includeControlIcon ? ControlIconComponent : undefined}
                components={{
                    Option: OptionComponent,
                }}
            />
            <div style={{display: 'none'}}>
                {options
                    .filter((option) => option.actionType === 'close')
                    .map((option) => {
                        return (
                            <div
                                key={option.value}
                                ref={(node) => {
                                    clickableDivRefs.current[option.value] = node;
                                }}
                                className='popover-opener-ajax'
                                data-url={option.url}
                                data-append-to={'.control-segment'}
                            />
                        );
                    })}
            </div>
        </>
    );
}

// When the lead is open or pending, there's nothing to gain
// by reselecting those two options. However, when the lead is
// won, lost, or cancelled, the options provide additional
// selections (such as changing outomces, or product values)
// so we should continue to show those.
function shouldHideSelectedOptions(status: string): boolean {
    return status === 'OPEN' || status === 'PENDING';
}

function OptionComponent(props: OptionProps) {
    return (
        <components.Option {...props}>
            <LeadStatusComponent
                value={props.data.value}
                label={props.data.label}
                // @ts-expect-error
                actionType={props.data.actionType}
            />
        </components.Option>
    );
}

const LeadStatusComponent = ({value, label}: {value: Status; label: string}) => {
    return (
        <div className={styles['option-container']}>
            <LeadStatusIcon status={value} />
            <div className={styles['option-title']}>{label}</div>
        </div>
    );
};
